<template>
	<div :class="{ 'tinymce-dark': isDark }">
		<editor
			v-if="ready"
			:key="editorKey"
			:id="instanceId"
			:value="internalValue"
			:init="editorConfig"
			:disabled="disabled"
			@input="onUpdate"
		/>
		<div v-else :style="{ height: '300px' }" />
	</div>
</template>

<script>
import tinymce from 'tinymce/tinymce'
import 'tinymce/themes/silver'
import 'tinymce/icons/default'
import 'tinymce/plugins/advlist'
import 'tinymce/plugins/autolink'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/link'
import 'tinymce/plugins/image'
import 'tinymce/plugins/charmap'
import 'tinymce/plugins/codesample'
import 'tinymce/plugins/table'
import 'tinymce/plugins/emoticons'
import 'tinymce/plugins/paste'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/autoresize'
import 'tinymce/plugins/imagetools'
import Editor from '@tinymce/tinymce-vue'

// Contatore a livello di modulo per generare un id univoco per ogni istanza
// dell'editor. TinyMCE tiene un registro globale degli editor indicizzato per id
// (tinymce.get(id)). Se due istanze condividono lo stesso id — o se un'istanza
// viene ricreata prima che la precedente sia stata rimossa dal registro (apri/
// chiudi rapido del dialog: l'init asincrono di TinyMCE non fa in tempo a
// completare prima del destroy, lasciando un editor "zombie" registrato) —
// tinymce.init salta l'inizializzazione e resta visibile la textarea grezza con
// l'HTML. Un id univoco per istanza elimina qualsiasi collisione.
let tinymceInstanceCounter = 0

const svgPath = (d) =>
	`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="${d}"/></svg>`

function normalizeContent(content) {
	if (!content) return content
	// Plain text (no HTML tags) → convert \n to <br>
	if (!/<[a-z][\s\S]*?>/i.test(content)) {
		return content.replace(/\n/g, '<br>')
	}
	return content
}

const mdiIcons = {
	'bold': 'M13.5,15.5H10V12.5H13.5A1.5,1.5 0 0,1 15,14A1.5,1.5 0 0,1 13.5,15.5M10,6.5H13A1.5,1.5 0 0,1 14.5,8A1.5,1.5 0 0,1 13,9.5H10M15.6,10.79C16.57,10.11 17.25,9 17.25,8C17.25,5.74 15.5,4 13.25,4H7V18H14.04C16.14,18 17.75,16.3 17.75,14.21C17.75,12.69 16.89,11.39 15.6,10.79Z',
	'italic': 'M10,4V7H12.21L8.79,15H6V18H14V15H11.79L15.21,7H18V4H10Z',
	'underline': 'M5,21H19V19H5V21M12,17A6,6 0 0,0 18,11V3H15.5V11A3.5,3.5 0 0,1 12,14.5A3.5,3.5 0 0,1 8.5,11V3H6V11A6,6 0 0,0 12,17Z',
	'strike-through': 'M3,14H21V12H3M5,4V7H10V10H14V7H19V4M10,19H14V16H10V19Z',
	'align-left': 'M3,3H21V5H3V3M3,7H15V9H3V7M3,11H21V13H3V11M3,15H15V17H3V15M3,19H21V21H3V19Z',
	'align-center': 'M3,3H21V5H3V3M7,7H17V9H7V7M3,11H21V13H3V11M7,15H17V17H7V15M3,19H21V21H3V19Z',
	'align-right': 'M3,3H21V5H3V3M9,7H21V9H9V7M3,11H21V13H3V11M9,15H21V17H9V15M3,19H21V21H3V19Z',
	'align-justify': 'M3,3H21V5H3V3M3,7H21V9H3V7M3,11H21V13H3V11M3,15H21V17H3V15M3,19H21V21H3V19Z',
	'quote': 'M10,7L8,11H11V17H5V11L7,7H10M18,7L16,11H19V17H13V11L15,7H18Z',
	'code-sample': 'M8,3A2,2 0 0,0 6,5V9A2,2 0 0,1 4,11H3V13H4A2,2 0 0,1 6,15V19A2,2 0 0,0 8,21H10V19H8V14A2,2 0 0,0 6,12A2,2 0 0,0 8,10V5H10V3M16,3A2,2 0 0,1 18,5V9A2,2 0 0,0 20,11H21V13H20A2,2 0 0,0 18,15V19A2,2 0 0,1 16,21H14V19H16V14A2,2 0 0,1 18,12A2,2 0 0,1 16,10V5H14V3H16Z',
	'ordered-list': 'M7,13V11H21V13H7M7,19V17H21V19H7M7,7V5H21V7H7M3,8V5H2V4H4V8H3M2,17V16H5V20H2V19H4V18.5H3V17.5H4V17H2M4.25,10A0.75,0.75 0 0,1 5,10.75C5,10.95 4.92,11.14 4.79,11.27L3.12,13H5V14H2V13.08L4,11H2V10H4.25Z',
	'unordered-list': 'M7,5H21V7H7V5M7,13V11H21V13H7M4,4.5A0.5,0.5 0 0,1 4.5,5A0.5,0.5 0 0,1 4,5.5A0.5,0.5 0 0,1 3.5,5A0.5,0.5 0 0,1 4,4.5M4,10.5A0.5,0.5 0 0,1 4.5,11A0.5,0.5 0 0,1 4,11.5A0.5,0.5 0 0,1 3.5,11A0.5,0.5 0 0,1 4,10.5M7,19V17H21V19H7M4,16.5A0.5,0.5 0 0,1 4.5,17A0.5,0.5 0 0,1 4,17.5A0.5,0.5 0 0,1 3.5,17A0.5,0.5 0 0,1 4,16.5Z',
	'outdent': 'M11,13H21V11H11M11,9H21V7H11M3,3V5H21V3M11,17H21V15H11M3,8L7,12L3,16V8M3,21H21V19H3V21Z',
	'indent': 'M11,13H21V11H11M11,9H21V7H11M3,3V5H21V3M11,17H21V15H11M3,16L7,12L3,8V16M3,21H21V19H3V21Z',
	'text-color': 'M9.62,12L12,5.67L14.37,12M11,3L5.5,17H7.75L8.87,14H15.12L16.25,17H18.5L13,3H11M3,20H21V22H3V20Z',
	'highlight-bg-color': 'M19,11.5C19,11.5 17,13.67 17,15A2,2 0 0,0 19,17A2,2 0 0,0 21,15C21,13.67 19,11.5 19,11.5M5.21,10L10,5.21L14.79,10M16.56,8.94L7.62,0L6.21,1.41L8.59,3.79L3.44,8.94C2.85,9.5 2.85,10.47 3.44,11.06L8.94,16.56C9.23,16.85 9.62,17 10,17C10.38,17 10.77,16.85 11.06,16.56L16.56,11.06C17.15,10.47 17.15,9.5 16.56,8.94Z',
	'link': 'M3.9,12C3.9,10.29 5.29,8.9 7,8.9H11V7H7A5,5 0 0,0 2,12A5,5 0 0,0 7,17H11V15.1H7C5.29,15.1 3.9,13.71 3.9,12M8,13H16V11H8V13M17,7H13V8.9H17C18.71,8.9 20.1,10.29 20.1,12C20.1,13.71 18.71,15.1 17,15.1H13V17H17A5,5 0 0,0 22,12A5,5 0 0,0 17,7Z',
	'image': 'M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z',
	'table': 'M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z',
	'emoji': 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M7,9.5C7,10.3 7.7,11 8.5,11C9.3,11 10,10.3 10,9.5C10,8.7 9.3,8 8.5,8C7.7,8 7,8.7 7,9.5M17,9.5C17,8.7 16.3,8 15.5,8C14.7,8 14,8.7 14,9.5C14,10.3 14.7,11 15.5,11C16.3,11 17,10.3 17,9.5M12,17.23C10.25,17.23 8.71,16.5 7.81,15.42L6.87,16.36C8.08,17.74 9.95,18.63 12,18.63C14.05,18.63 15.92,17.74 17.13,16.36L16.19,15.42C15.29,16.5 13.75,17.23 12,17.23Z',
	'remove-formatting': 'M3.27,5L2,6.27L8.97,13.24L6.5,19H9L11.07,14.34L16.73,20L18,18.73M6,5V5.18L8.82,8H11.22L10.5,9.68L12.6,11.78L14.21,8H20V5H6Z',
	'fullscreen': 'M5,5H10V7H7V10H5V5M14,5H19V10H17V7H14V5M17,14H19V19H14V17H17V14M10,17V19H5V14H7V17H10Z',
	'fullscreen-exit': 'M14,14H19V16H16V19H14V14M5,14H10V19H8V16H5V14M8,5H10V10H5V8H8V5M19,8V10H14V5H16V8H19Z',
	'chevron-down': 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z',
	'resize-handle': 'M9,3L5,7H8V14H10V7H13M16,17H13V10H11V17H8L12,21L16,17Z',
	'rotate-left': 'M13,4.07V1L8.45,5.55L13,10V6.09C15.84,6.57 18,9.03 18,12C18,14.97 15.84,17.43 13,17.91V19.93C16.95,19.44 20,16.08 20,12C20,7.92 16.95,4.56 13,4.07M7.1,18.32C8.26,19.22 9.61,19.76 11,19.93V17.9C10.13,17.75 9.29,17.41 8.54,16.87L7.1,18.32M6.09,13H4.07C4.24,14.39 4.79,15.73 5.69,16.89L7.1,15.47C6.58,14.72 6.23,13.88 6.09,13M7.11,8.53L5.7,7.11C4.8,8.27 4.24,9.61 4.07,11H6.09C6.23,10.13 6.58,9.28 7.11,8.53Z',
	'rotate-right': 'M16.89,15.5L18.32,16.89C19.21,15.73 19.76,14.39 19.93,13H17.91C17.76,13.87 17.43,14.72 16.89,15.5M13,17.9V19.92C14.39,19.75 15.74,19.21 16.9,18.31L15.46,16.87C14.71,17.41 13.87,17.76 13,17.9M19.93,11C19.76,9.61 19.22,8.26 18.32,7.1L16.89,8.54C17.43,9.28 17.76,10.13 17.91,11M15.55,5.55L11,1V4.07C7.06,4.56 4,7.92 4,12C4,16.08 7.05,19.44 11,19.93V17.91C8.16,17.43 6,14.97 6,12C6,9.03 8.16,6.57 11,6.09V10L15.55,5.55Z',
	'flip-horizontally': 'M3,15V17H5V15M3,19V21H5V19M7,15V21H9V15M11,15V21H13V15M3,9V13H13V9M15,3V21H17V3M19,15V21H21V15M19,9V13H21V9M19,3V7H21V3M3,3V7H13V3H3Z',
	'flip-vertically': 'M21,9H19V7H21M21,3H19V5H21M15,3H9V5H15M15,21H9V19H15M21,21H19V19H21M3,15H21V9H3M21,13H19V11H21M5,3V21H7V3M3,3V7H5V3H3M3,19H5V21H3V19Z',
	'image-options': 'M19.5,13.5L21,15L15,21H13.5V19.5L19.5,13.5M22.85,13.19C23.04,13.38 23.04,13.69 22.85,13.88L21.68,15.05L19.95,13.32L21.12,12.15C21.31,11.96 21.62,11.96 21.81,12.15L22.85,13.19M11,19H4A1,1 0 0,1 3,18V6A1,1 0 0,1 4,5H20A1,1 0 0,1 21,6V13L19,15V7H5V17H11V19Z',
}

export default {
	name: 'TinymceEditor',

	components: { Editor },

	props: {
		value: { type: String, default: '' },
		editorId: { type: String, default: 'tinymce-editor' },
		disabled: { type: Boolean, default: false },
	},

	data() {
		return {
			internalValue: normalizeContent(this.value) || '',
			// Id univoco per questa istanza: l'editorId passato resta come prefisso
			// leggibile, ma il suffisso garantisce l'unicità nel registro globale.
			instanceId: `${this.editorId}-${tinymceInstanceCounter++}`,
			// Ritardiamo l'inizializzazione di TinyMCE di 300ms per permettere al dialog
			// di Vuetify di completare la sua animazione (~225ms) prima che l'iframe
			// venga creato. In caso contrario Vuetify sposta il DOM durante l'animazione
			// e l'iframe viene ricaricato (about:blank), perdendo il contenuto.
			ready: false,
		}
	},

	mounted() {
		this.$_readyTimer = setTimeout(() => {
			this.ready = true
		}, 0)
	},

	beforeDestroy() {
		this.$_destroyed = true
		clearTimeout(this.$_readyTimer)
		clearTimeout(this.$_initTimer)
		// Rimuove esplicitamente l'istanza TinyMCE dal registro globale: senza
		// questo, chiudendo e riaprendo velocemente il dialog si accumulano editor
		// "zombie" (memory leak) e nei casi limite bloccano la re-inizializzazione.
		const editor = tinymce.get(this.instanceId)
		if (editor) {
			editor.remove()
		}
		if (this.$_auxFocusinFix) {
			document.body.removeEventListener('focusin', this.$_auxFocusinFix)
		}
		if (this.$_auxMousedownFix) {
			document.body.removeEventListener('mousedown', this.$_auxMousedownFix, true)
		}
	},

	computed: {
		isDark() {
			return this.$vuetify.theme.dark
		},

		editorKey() {
			return `${this.instanceId}-${this.isDark ? 'dark' : 'light'}`
		},

		editorConfig() {
			return {
				skin_url: this.isDark
					? '/tinymce/skins/ui/oxide-dark'
					: '/tinymce/skins/ui/oxide',
				content_css: this.isDark
					? '/tinymce/skins/content/dark/content.min.css'
					: '/tinymce/skins/content/default/content.min.css',
				content_style: 'body { font-family: Roboto, sans-serif; font-size: 14px; } p { margin: 0; }',
				autoresize_min_height: 250,
				autoresize_overflow_padding: 10,
				menubar: false,
				plugins: 'advlist autolink lists link image charmap codesample table emoticons paste fullscreen wordcount autoresize imagetools',
				toolbar: 'formatselect | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | blockquote codesample | numlist bullist | outdent indent | forecolor backcolor | link image table | emoticons | removeformat',
				emoticons_database_url: '/tinymce/plugins/emoticons/js/emojis.min.js',
				block_formats: 'Paragrafo=p; Intestazione 1=h1; Intestazione 2=h2; Intestazione 3=h3; Intestazione 4=h4; Intestazione 5=h5; Intestazione 6=h6',
				language: 'it',
				language_url: '/tinymce/langs/it.js',
				// Correttore ortografico nativo del browser (gratuito): mostra le
				// sottolineature rosse sotto le parole errate mentre si scrive. Il lang
				// del body dell'editor viene impostato a 'it' in onEditorInit per far
				// preferire il dizionario italiano. I suggerimenti di correzione sono
				// accessibili col menu contestuale nativo (click destro su testo semplice;
				// su link/immagini/tabelle resta il menu di TinyMCE).
				browser_spellcheck: true,
				branding: false,
				deprecation_warnings: false,
				object_resizing: true,
				resize: false,
				paste_data_images: true,
				automatic_uploads: true,
				images_upload_handler: this.imagesUploadHandler,
				file_picker_types: 'image',
				file_picker_callback: this.filePickerCallback,
				setup: this.editorSetup,
				init_instance_callback: this.onEditorInit,
			}
		},
	},

	watch: {
		value(val) {
			const normalized = normalizeContent(val)
			if (normalized !== this.internalValue) {
				this.internalValue = normalized
				// Aggiorna direttamente il contenuto dell'editor se è già inizializzato.
				// Necessario quando il valore arriva dopo l'init (dialog + caricamento dati asincrono).
				const editor = tinymce.get(this.instanceId)
				if (editor && normalized !== editor.getContent()) {
					editor.setContent(normalized || '')
				}
			}
		},
	},

	methods: {
		onEditorInit(editor) {
			// Se il componente è già stato distrutto prima che l'init asincrono di
			// TinyMCE completasse (apri/chiudi rapido del dialog), rimuoviamo subito
			// l'editor appena creato: altrimenti resterebbe registrato nel registro
			// globale come istanza "zombie" senza DOM valido.
			if (this.$_destroyed) {
				editor.remove()
				return
			}

			// Imposta la lingua del corpo modificabile a italiano così il correttore
			// nativo del browser usa il dizionario italiano (Chrome/Edge rispettano
			// l'attributo lang per la scelta del dizionario di spellcheck).
			const body = editor.getBody()
			if (body) {
				body.setAttribute('lang', 'it')
			}

			this.$_initTimer = setTimeout(() => {
				// Se il componente è stato distrutto (e l'editor rimosso in beforeDestroy)
				// entro i 100ms, non tocchiamo più l'istanza: getWin/getContent/setContent
				// su un editor rimosso genererebbero errori runtime.
				if (this.$_destroyed) return
				const win = editor.getWin()
				if (win) {
					win.dispatchEvent(new Event('resize'))
				}
				// Fallback: se il contenuto non è stato caricato correttamente, lo reimpostiamo
				if (this.internalValue && editor.getContent() === '') {
					editor.setContent(normalizeContent(this.internalValue))
				}
			}, 100)

			// Fix TinyMCE floating UI in Vuetify dialog:
			// TinyMCE rende tutti gli elementi floating (toolbar contestuale .tox-pop,
			// table picker, dropdown menu, ecc.) in .tox-tinymce-aux nel body — fuori
			// dal DOM del dialog Vuetify.
			//
			// Vuetify retainFocus ascolta `focusin` su `window` (bubble phase): se il
			// focus finisce fuori dal dialog, forza il focus dentro → tooltip inattesi,
			// selezione/stato TinyMCE persi.
			//
			// Fix A — stopPropagation su focusin (bubble):
			//   Blocca il focusin PRIMA che raggiunga il listener di Vuetify su window.
			//   Copre sia i focus programmatici di TinyMCE (apertura dropdown)
			//   sia i click utente che spostano il focus nell'aux.
			this.$_auxFocusinFix = (e) => {
				if (e.target.closest('.tox-tinymce-aux')) {
					e.stopPropagation()
				}
			}
			document.body.addEventListener('focusin', this.$_auxFocusinFix)

			// Fix B — preventDefault su mousedown (capture):
			//   Per imagetools: impedisce al browser di spostare il focus dall'iframe
			//   all'elemento cliccato (il click event avviene comunque). Mantiene la
			//   selezione immagine attiva nell'iframe durante l'esecuzione del comando.
			//   Eccezione: input/textarea/select nei dialog TinyMCE devono ricevere focus.
			this.$_auxMousedownFix = (e) => {
				if (
					e.target.closest('.tox-tinymce-aux') &&
					!['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)
				) {
					e.preventDefault()
				}
			}
			document.body.addEventListener('mousedown', this.$_auxMousedownFix, true)
		},

		insertContent(text) {
			const editor = tinymce.get(this.instanceId)
			if (!editor) return
			editor.focus()
			editor.execCommand('mceInsertContent', false, text)
		},

		onUpdate(val) {
			this.internalValue = val
			this.$emit('input', val)
		},

		imagesUploadHandler(blobInfo, success, failure) {
			const reader = new FileReader()
			reader.onload = (e) => {
				this.optimizeDataURL(e.target.result)
					.then(success)
					.catch(() => failure('Errore durante l\'ottimizzazione dell\'immagine'))
			}
			reader.onerror = () => failure('Errore di lettura del file')
			reader.readAsDataURL(blobInfo.blob())
		},

		filePickerCallback(callback, _value, meta) {
			if (meta.filetype === 'image') {
				const input = document.createElement('input')
				input.setAttribute('type', 'file')
				input.setAttribute('accept', 'image/*')
				input.onchange = () => {
					const file = input.files[0]
					if (!file) return
					const reader = new FileReader()
					reader.onload = (e) => {
						this.optimizeDataURL(e.target.result).then((dataurl) => {
							callback(dataurl, { alt: file.name })
						})
					}
					reader.onerror = () => console.error('Errore di lettura del file')
					reader.readAsDataURL(file)
				}
				input.click()
			}
		},

		editorSetup(editor) {
			// Sostituiamo le icone default con le icone MDI
			const existingIcons = (tinymce.IconManager.get('default') || {}).icons || {}
			const overrides = {}
			Object.keys(mdiIcons).forEach((name) => {
				overrides[name] = svgPath(mdiIcons[name])
			})
			tinymce.IconManager.add('default', {
				icons: { ...existingIcons, ...overrides },
			})

			try {
				editor.ui.registry.addButton('blockquote', {
					icon: 'quote',
					tooltip: 'Citazione',
					onAction: () => editor.execCommand('FormatBlock', false, 'blockquote'),
				})
			} catch (e) {
				// blockquote button già registrato
			}

			editor.on('NodeChange', () => {
				editor.dom.select('img').forEach((img) => {
					if (!img.style.padding) {
						editor.dom.setStyle(img, 'padding', '8px')
					}
				})
			})

			// Intercetta il paste di immagini copiate da web: il clipboard contiene
			// sia il blob immagine sia l'HTML con <img src="https://...">.
			// TinyMCE preferisce l'HTML → l'immagine resta con URL esterno che non
			// funziona in stampa/PDF. Qui forziamo l'uso del blob e lo embeddiamo
			// come base64 tramite optimizeDataURL.
			editor.on('paste', (e) => {
				const clipboardData = e.clipboardData
				if (!clipboardData) return

				let imageItem = null
				for (let i = 0; i < clipboardData.items.length; i++) {
					if (clipboardData.items[i].type.startsWith('image/')) {
						imageItem = clipboardData.items[i]
						break
					}
				}
				if (!imageItem) return

				const html = clipboardData.getData('text/html')
				if (html) {
					const tempDiv = document.createElement('div')
					tempDiv.innerHTML = html
					const imgs = tempDiv.querySelectorAll('img')
					const hasOnlyImages = tempDiv.textContent.trim() === '' && imgs.length > 0
					if (!hasOnlyImages) return
				}

				e.preventDefault()
				const blob = imageItem.getAsFile()
				const reader = new FileReader()
				reader.onload = (ev) => {
					this.optimizeDataURL(ev.target.result).then(dataUrl => {
						editor.execCommand('mceInsertContent', false,
							'<img src="' + dataUrl + '" style="padding: 8px;" />')
					})
				}
				reader.readAsDataURL(blob)
			})
		},

		optimizeDataURL(data) {
			return new Promise((resolve, reject) => {
				const img = document.createElement('img')
				img.src = data
				img.onload = () => {
					const canvas = document.createElement('canvas')
					const MAX_WIDTH = 600
					const MAX_HEIGHT = 600
					let width = img.width
					let height = img.height
					if (width > height) {
						if (width > MAX_WIDTH) {
							height *= MAX_WIDTH / width
							width = MAX_WIDTH
						}
					} else {
						if (height > MAX_HEIGHT) {
							width *= MAX_HEIGHT / height
							height = MAX_HEIGHT
						}
					}
					canvas.width = width
					canvas.height = height
					const ctx = canvas.getContext('2d')
					ctx.drawImage(img, 0, 0, width, height)
					resolve(canvas.toDataURL('image/jpeg'))
				}
				img.onerror = () => reject(new Error('Impossibile caricare l\'immagine'))
			})
		},
	},
}
</script>

<style scoped>
/* ── Light mode ─────────────────────────────────────────────────────────── */
::v-deep .tox.tox-tinymce {
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 4px;
}

::v-deep .tox .tox-toolbar,
::v-deep .tox .tox-toolbar__overflow,
::v-deep .tox .tox-toolbar-overlord {
	background: #ffffff;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

::v-deep .tox .tox-toolbar__primary {
	background: #ffffff;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

::v-deep .tox .tox-tbtn {
	cursor: pointer;
}

::v-deep .tox .tox-tbtn:hover {
	background: rgba(255, 193, 7, 0.12);
	border-radius: 4px;
}

::v-deep .tox .tox-tbtn--enabled,
::v-deep .tox .tox-tbtn--enabled:hover {
	background: rgba(255, 193, 7, 0.28);
	border-radius: 4px;
}

::v-deep .tox .tox-tbtn:active {
	background: rgba(255, 193, 7, 0.4);
	border-radius: 4px;
}

::v-deep .tox .tox-tbtn svg {
	fill: rgba(0, 0, 0, 0.7);
}

::v-deep .tox .tox-tbtn:hover svg,
::v-deep .tox .tox-tbtn--enabled svg {
	fill: #e6ac00;
}

::v-deep .tox .tox-statusbar {
	border-top: 1px solid rgba(0, 0, 0, 0.08);
	background: #ffffff;
	color: rgba(0, 0, 0, 0.54);
}

::v-deep .tox .tox-statusbar a,
::v-deep .tox .tox-statusbar__path-item,
::v-deep .tox .tox-statusbar__wordcount {
	color: rgba(0, 0, 0, 0.54);
}

::v-deep .tox .tox-toolbar__group {
	border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}

/* ── Dark mode ──────────────────────────────────────────────────────────── */
.tinymce-dark ::v-deep .tox.tox-tinymce {
	border: 1px solid rgba(255, 255, 255, 0.12);
}

.tinymce-dark ::v-deep .tox .tox-toolbar,
.tinymce-dark ::v-deep .tox .tox-toolbar__overflow,
.tinymce-dark ::v-deep .tox .tox-toolbar-overlord {
	background: #424242;
	border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.tinymce-dark ::v-deep .tox .tox-toolbar__primary {
	background: #424242;
	border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.tinymce-dark ::v-deep .tox .tox-tbtn svg {
	fill: rgba(255, 255, 255, 0.7);
}

.tinymce-dark ::v-deep .tox .tox-tbtn:hover svg,
.tinymce-dark ::v-deep .tox .tox-tbtn--enabled svg {
	fill: #ffc107;
}

.tinymce-dark ::v-deep .tox .tox-statusbar {
	border-top: 1px solid rgba(255, 255, 255, 0.08);
	background: #424242;
	color: rgba(255, 255, 255, 0.54);
}

.tinymce-dark ::v-deep .tox .tox-statusbar a,
.tinymce-dark ::v-deep .tox .tox-statusbar__path-item,
.tinymce-dark ::v-deep .tox .tox-statusbar__wordcount {
	color: rgba(255, 255, 255, 0.54);
}

.tinymce-dark ::v-deep .tox .tox-toolbar__group {
	border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
}
</style>

<style>
/* ── TinyMCE dialogs — Vuetify styling ──────────────────────────────────── */
/* I dialog vengono renderizzati da TinyMCE come portali nel <body>, fuori dal
   DOM del componente, quindi servono stili globali (non scoped). */

.tox .tox-dialog {
	border-radius: 4px !important;
}

.tox .tox-dialog__header {
	padding: 12px 16px 12px !important;
}

.tox .tox-dialog__footer {
	padding: 8px 16px !important;
}

/* Bottone primario (Salva / OK) → amber */
.tox .tox-button:not(.tox-button--secondary):not(.tox-button--naked) {
	background-color: #ffc107 !important;
	border-color: #ffc107 !important;
	color: rgba(0, 0, 0, 0.87) !important;
	border-radius: 4px !important;
	cursor: pointer !important;
}

.tox .tox-button:not(.tox-button--secondary):not(.tox-button--naked):hover,
.tox .tox-button:not(.tox-button--secondary):not(.tox-button--naked):focus {
	background-color: #e6ac00 !important;
	border-color: #e6ac00 !important;
	box-shadow: none !important;
}

/* Bottone secondario (Annulla) */
.tox .tox-button--secondary {
	border-radius: 4px !important;
	cursor: pointer !important;
}

/* Focus degli input → amber */
.tox .tox-textfield:focus,
.tox .tox-textarea:focus {
	border-color: #ffc107 !important;
	box-shadow: none !important;
}

/* Checkbox → amber */
.tox .tox-checkbox__icons .tox-checkbox-icon__checked svg {
	fill: #ffc107 !important;
}
</style>
